import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SectionTitle from '../components/SectionTitle';
import ImageContainer from '../components/ImageContainer';
import HeroBackground from '../components/HeroBackground';

const HomePage: React.FC = () => {
  const sectionsRef = useRef<(HTMLElement | null)[]>([]);

  // Set up intersection observer for animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          entry.target.querySelectorAll('.fade-in').forEach((el, index) => {
            (el as HTMLElement).style.animationDelay = `${index * 200}ms`;
            el.classList.add('animate');
          });
        }
      });
    }, observerOptions);

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      sectionsRef.current.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  // Key features to highlight on the homepage
  const features = [
    {
      title: 'Websites',
      description: 'Custom websites tailored to your needs.',
      icon: (
        <svg className="w-8 h-8 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      link: '/loesungen',
    },
    {
      title: 'Booking Systems',
      description: 'Integrated booking solutions for your services.',
      icon: (
        <svg className="w-8 h-8 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      link: '/loesungen',
    },
    {
      title: 'Idea Management',
      description: 'Submit and track your ideas.',
      icon: (
        <svg className="w-8 h-8 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      link: '/ideen',
    },
  ];

  // Target groups to highlight
  const targetGroups = [
    { name: 'Gastronomy', icon: '🍽️', path: '/fuer-wen', image: '/images/target/target-gastronomy_left.jpg', textPosition: 'left' },
    { name: 'Retail', icon: '🛍️', path: '/fuer-wen', image: '/images/target/target-retail.jpg', textPosition: 'right' },
    { name: 'Coaches', icon: '📊', path: '/fuer-wen', image: '/images/target/target-coach.jpg', textPosition: 'right' },
    { name: 'Creatives', icon: '🎨', path: '/fuer-wen', image: '/images/target/target-creative.jpg', textPosition: 'left' },
  ];

  const targetAudiences = [
    { name: 'Gastronomy', path: '/target-groups' },
    { name: 'Retail', path: '/target-groups' },
    { name: 'Coaches', path: '/target-groups' },
    { name: 'Creatives', path: '/target-groups' },
    { name: 'Seniors', path: '/seniors' },
    { name: 'Clubs', path: '/target-groups' }
  ];

  return (
    <main className="bg-light dark:bg-deepblue-900 transition-colors duration-300">
      {/* Hero Section with full background image */}
      <section 
        ref={(el) => sectionsRef.current[0] = el}
        className="snap-section"
      >
        <HeroBackground 
          imageSrc="/images/hero/forkit_hero_left.jpg"
          position="left"
          overlayOpacity="medium"
        >
          <div className="max-w-3xl">
            <h1 className="homepage-hero-title mb-6 fade-in">
              Your Digital Independence Starts Here
            </h1>
            <p className="homepage-hero-subtitle fade-in">
              We build fair and transparent solutions for small businesses.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 fade-in">
              <Link 
                to="/warteliste" 
                className="bg-ocean-600 hover:bg-ocean-700 text-white px-6 py-3 rounded-sm font-lexend font-medium text-base shadow-md transition-all"
              >
                Join Waitlist
              </Link>
              <Link 
                to="/manifest" 
                className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-6 py-3 rounded-sm font-lexend font-medium text-base hover:bg-white/20 transition-all"
              >
                Our Manifest
              </Link>
            </div>
          </div>
        </HeroBackground>
      </section>

      {/* Manifest Intro Section with background image */}
      <section 
        ref={(el) => sectionsRef.current[1] = el}
        className="snap-section bg-gradient-to-br from-slate-50 via-ocean-50 to-slate-50 dark:from-deepblue-900 dark:via-ocean-900/20 dark:to-deepblue-900 py-24 transition-all duration-700"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="homepage-hero-title mb-12 fade-in">
              Our Manifest 1.1
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div 
                className="bg-white dark:bg-deepblue-800 p-8 rounded-lg shadow-lg transform hover:-translate-y-2 transition-all duration-500 fade-in group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-ocean-100/20 to-transparent dark:from-ocean-500/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left"></div>
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-ocean-100 dark:bg-ocean-900/50 rounded-lg mb-4 flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-500">
                    <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h3 className="font-lexend font-semibold text-xl mb-4 text-slate-900 dark:text-white group-hover:text-ocean-600 dark:group-hover:text-ocean-400 transition-colors">
                    The Problem
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    The digital world no longer belongs to the people who use it - but to the platforms that control it.
                  </p>
                </div>
              </div>
              
              <div 
                className="bg-white dark:bg-deepblue-800 p-8 rounded-lg shadow-lg transform hover:-translate-y-2 transition-all duration-500 fade-in group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-ocean-100/20 to-transparent dark:from-ocean-500/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left"></div>
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-ocean-100 dark:bg-ocean-900/50 rounded-lg mb-4 flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-500">
                    <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="font-lexend font-semibold text-xl mb-4 text-slate-900 dark:text-white group-hover:text-ocean-600 dark:group-hover:text-ocean-400 transition-colors">
                    The Reality
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    Visibility has become a commodity - and the largest platforms hold the monopoly.
                  </p>
                </div>
              </div>
              
              <div 
                className="bg-white dark:bg-deepblue-800 p-8 rounded-lg shadow-lg transform hover:-translate-y-2 transition-all duration-500 fade-in group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-ocean-100/20 to-transparent dark:from-ocean-500/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left"></div>
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-ocean-100 dark:bg-ocean-900/50 rounded-lg mb-4 flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-500">
                    <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <h3 className="font-lexend font-semibold text-xl mb-4 text-slate-900 dark:text-white group-hover:text-ocean-600 dark:group-hover:text-ocean-400 transition-colors">
                    Our Vision
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    We believe things can be different. Digital independence should be accessible to everyone.
                  </p>
                </div>
              </div>
              
              <div 
                className="bg-white dark:bg-deepblue-800 p-8 rounded-lg shadow-lg transform hover:-translate-y-2 transition-all duration-500 fade-in group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-ocean-100/20 to-transparent dark:from-ocean-500/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left"></div>
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-ocean-100 dark:bg-ocean-900/50 rounded-lg mb-4 flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-500">
                    <svg className="w-6 h-6 text-ocean-600 dark:text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h3 className="font-lexend font-semibold text-xl mb-4 text-slate-900 dark:text-white group-hover:text-ocean-600 dark:group-hover:text-ocean-400 transition-colors">
                    Our Mission
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    Building tools and solutions that empower people to take control of their digital presence.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-12 fade-in">
              <Link 
                to="/manifest" 
                className="inline-flex items-center font-lexend font-medium text-ocean-600 dark:text-ocean-400 text-lg hover:text-ocean-700 dark:hover:text-ocean-300 transition-colors group"
              >
                Read Full Manifest <span className="ml-2 transform group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features/CTA Boxes - Grid layout with individual cards */}
      <section 
        ref={(el) => sectionsRef.current[2] = el}
        className="snap-section bg-slate-50 dark:bg-deepblue-900 py-16 flex items-center"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-12 fade-in">
            <h2 className="headline-section">
              Our Solutions
            </h2>
            <p className="body-normal mt-3">
              Digital independence through tailor-made solutions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-deepblue-800 p-6 rounded-sm shadow-sm border border-gray-200 dark:border-deepblue-700 hover:shadow-md transition-all fade-in"
              >
                <div className="mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-lexend font-semibold text-xl mb-2 text-gray-900 dark:text-white">
                  {feature.title}
                </h3>
                <p className="body-small mb-6">
                  {feature.description}
                </p>
                <Link 
                  to={feature.link} 
                  className="font-lexend text-sm font-medium text-ocean-600 dark:text-ocean-400 hover:underline inline-flex items-center"
                >
                  Mehr erfahren <span className="ml-1">→</span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Feature - Solution section with background image */}
      <section 
        ref={(el) => sectionsRef.current[3] = el}
        className="snap-section"
      >
        <HeroBackground 
          imageSrc="/images/solution/solution_left.jpeg"
          position="left"
          overlayOpacity="medium"
        >
          <div className="max-w-3xl">
            <h2 className="homepage-hero-title mb-6 fade-in">
              Digital Solutions for Small Businesses
            </h2>
            <p className="homepage-hero-subtitle mb-8 fade-in">
              We develop digital solutions tailored to the needs of small and medium-sized businesses. No unnecessary features, no hidden costs.
            </p>
            <Link 
              to="/solutions" 
              className="inline-flex items-center font-lexend text-white text-lg hover:text-white/80 transition-colors fade-in"
            >
              Explore All Solutions <span className="ml-2">→</span>
            </Link>
          </div>
        </HeroBackground>
      </section>

      {/* Target Groups Section */}
      <section 
        ref={(el) => sectionsRef.current[4] = el}
        className="snap-section bg-gray-50 dark:bg-deepblue-900 py-16 flex items-center"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-start mb-12 fade-in">
            <div>
              <h2 className="headline-section">
                For Whom?
              </h2>
              <p className="body-normal mt-3 max-w-2xl">
                We help various industries and people affected by platform dependency
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {targetGroups.map((group, index) => (
              <Link
                key={index}
                to={group.path}
                className="group overflow-hidden rounded-sm border border-gray-200 dark:border-deepblue-700 transition-all fade-in"
              >
                <ImageContainer
                  src={group.image}
                  alt={group.name}
                  aspectRatio="4:3"
                  overlay
                >
                  <div className={`absolute inset-0 flex items-center justify-${group.textPosition === 'left' ? 'start' : 'end'} p-6`}>
                    <span className="text-on-image-heading px-4 text-center text-xl">
                      {group.name}
                    </span>
                  </div>
                </ImageContainer>
                <div className="p-4 bg-white dark:bg-deepblue-800 flex items-center space-x-3">
                  <span className="text-2xl">{group.icon}</span>
                  <span className="font-lexend font-medium text-gray-900 dark:text-white group-hover:text-ocean-600 dark:group-hover:text-ocean-400 transition-colors">
                    {group.name}
                  </span>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="mt-6 flex flex-wrap gap-3 justify-center fade-in">
            {targetAudiences.map((audience, index) => (
              <Link
                key={index}
                to={audience.path}
                className="px-4 py-2 rounded-sm bg-white dark:bg-deepblue-800 border border-gray-200 dark:border-deepblue-700 font-lexend font-medium text-gray-900 dark:text-white hover:text-ocean-600 dark:hover:text-ocean-400 transition-colors"
              >
                {audience.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Stories Section */}
      <section 
        ref={(el) => sectionsRef.current[5] = el}
        className="snap-section bg-white dark:bg-deepblue-800 py-16 flex items-center"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-12 fade-in">
            <h2 className="headline-section text-center">
              Success Stories
            </h2>
            <p className="body-normal mt-3 text-center">
              Learn how we helped other businesses achieve digital independence
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-deepblue-800 rounded-sm shadow-sm border border-gray-200 dark:border-deepblue-700 overflow-hidden fade-in">
              <div className="h-48">
                <img 
                  src="/images/content/customer-story_hero_left.jpg" 
                  alt="Customer story 1" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="font-lexend font-semibold text-xl mb-3 text-gray-900 dark:text-white">
                  Vom Foodora-Lieferanten zum eigenen Shop
                </h3>
                <p className="body-small mb-4">
                  Wie ein kleines Restaurant sich von Lieferplattformen unabhängig gemacht hat.
                </p>
                <Link 
                  to="/your-story" 
                  className="font-lexend text-sm font-medium text-ocean-600 dark:text-ocean-400 hover:underline inline-flex items-center"
                >
                  Read Story <span className="ml-1">→</span>
                </Link>
              </div>
            </div>
            
            <div className="bg-white dark:bg-deepblue-800 rounded-sm shadow-sm border border-gray-200 dark:border-deepblue-700 overflow-hidden fade-in">
              <div className="h-48">
                <img 
                  src="/images/content/customer-story3_hero_left.jpg" 
                  alt="Customer story 2" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="font-lexend font-semibold text-xl mb-3 text-gray-900 dark:text-white">
                  Fotografin findet Kunden ohne Instagram
                </h3>
                <p className="body-small mb-4">
                  Wie eine selbständige Fotografin ihre eigene digitale Präsenz aufgebaut hat.
                </p>
                <Link 
                  to="/your-story" 
                  className="font-lexend text-sm font-medium text-ocean-600 dark:text-ocean-400 hover:underline inline-flex items-center"
                >
                  Read Story <span className="ml-1">→</span>
                </Link>
              </div>
            </div>
            
            <div className="bg-white dark:bg-deepblue-800 rounded-sm shadow-sm border border-gray-200 dark:border-deepblue-700 overflow-hidden fade-in">
              <div className="h-48">
                <img 
                  src="/images/content/restaurant_right.jpeg" 
                  alt="Customer story 3" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="font-lexend font-semibold text-xl mb-3 text-gray-900 dark:text-white">
                  Vom Etsy-Shop zur eigenen Plattform
                </h3>
                <p className="body-small mb-4">
                  Wie ein kleines Handwerksunternehmen nach Jahren auf Etsy unabhängig wurde.
                </p>
                <Link 
                  to="/your-story" 
                  className="font-lexend text-sm font-medium text-ocean-600 dark:text-ocean-400 hover:underline inline-flex items-center"
                >
                  Read Story <span className="ml-1">→</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section with background image */}
      <section 
        ref={(el) => sectionsRef.current[6] = el}
        className="snap-section-last"
      >
        <HeroBackground 
          imageSrc="/images/content/forkit_restaurant_tablet.jpg"
          position="left"
          overlayOpacity="medium"
        >
          <div className="max-w-3xl">
            <h2 className="homepage-hero-title mb-6 fade-in">
              Become Part of the Movement
            </h2>
            <p className="homepage-hero-subtitle mb-8 fade-in">
              We want to reclaim digital independence - for you and your business.
            </p>
            <div className="flex flex-wrap gap-4 fade-in">
              <Link 
                to="/warteliste" 
                className="bg-ocean-600 hover:bg-ocean-700 text-white px-6 py-3 rounded-sm font-lexend font-medium text-base shadow-md transition-all"
              >
                Waitlist
              </Link>
              <Link 
                to="/your-story" 
                className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-6 py-3 rounded-sm font-lexend font-medium text-base hover:bg-white/20 transition-all"
              >
                Your Story
              </Link>
            </div>
          </div>
        </HeroBackground>
      </section>

      {/* Navigation dots */}
      <div className="fixed right-6 top-1/2 transform -translate-y-1/2 z-50 hidden md:block">
        <div className="flex flex-col space-y-3">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <button
              key={i}
              onClick={() => {
                if (sectionsRef.current[i]) {
                  sectionsRef.current[i]?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="w-3 h-3 rounded-full bg-gray-300 hover:bg-primary transition-all duration-300"
              aria-label={`Scroll to section ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </main>
  );
};

export default HomePage; 